import React from 'react';
import { Card } from './ui/card';
import { 
  Building2, 
  ShoppingBag, 
  Stethoscope, 
  Cpu, 
  Utensils, 
  Palette,
  GraduationCap,
  Dumbbell
} from 'lucide-react';

const IndustriesSection = () => {
  const industries = [
    {
      icon: <Cpu className="w-12 h-12" />,
      title: "Technology & Software",
      description: "Specialized trademark protection for tech startups, SaaS platforms, mobile apps, software companies, and digital product creators. We understand the fast-paced tech industry and provide expedited services for app names, software brands, tech product lines, and digital service marks. Our experience includes protecting AI platforms, cloud services, cybersecurity brands, and fintech applications. We help you secure your brand before product launch and scale protection as you grow.",
      image: "https://images.unsplash.com/photo-1580894907795-ac8e1838d40d"
    },
    {
      icon: <ShoppingBag className="w-12 h-12" />,
      title: "Retail & E-Commerce",
      description: "Comprehensive brand protection for brick-and-mortar retailers, online stores, marketplace sellers, and omnichannel businesses. We protect store names, private label brands, product lines, e-commerce platforms, and retail service marks. Our services include anti-counterfeiting strategies, marketplace brand registry assistance (Amazon, eBay, Etsy), and protection against unauthorized sellers. We help fashion brands, consumer goods companies, and retail chains build strong trademark portfolios that prevent infringement.",
      image: "https://images.unsplash.com/36/yJl7OB3sSpOdEIpHhZhd_DSC_1929_1.jpg"
    },
    {
      icon: <Stethoscope className="w-12 h-12" />,
      title: "Healthcare & Wellness",
      description: "Navigate complex healthcare trademark requirements for medical practices, hospitals, clinics, pharmaceutical companies, medical device manufacturers, telehealth platforms, and wellness brands. We ensure compliance with FDA regulations and healthcare advertising rules while securing strong trademark protection. Our expertise covers practice names, drug brands, medical device marks, health apps, wellness programs, and healthcare service marks across all specialties.",
      image: "https://images.unsplash.com/photo-1666886573590-5815157da865"
    },
    {
      icon: <Building2 className="w-12 h-12" />,
      title: "Manufacturing & Industrial",
      description: "Protect manufacturing brands, industrial product names, machinery trademarks, and B2B service marks for domestic and international markets. We assist manufacturers, industrial suppliers, equipment makers, and distribution companies in building recognizable brand identities. Our services include protection for product lines, company names, industrial processes, and trade dress. We understand supply chain complexities and help prevent unauthorized use by distributors and competitors.",
      image: "https://images.unsplash.com/photo-1717386255773-1e3037c81788"
    },
    {
      icon: <Utensils className="w-12 h-12" />,
      title: "Food & Beverage",
      description: "Complete trademark services for restaurants, food manufacturers, beverage brands, catering services, food trucks, breweries, wineries, and culinary brands. Protect restaurant names, food product brands, signature dishes, beverage formulations, packaging designs, and culinary concepts. We help navigate FDA labeling requirements while securing trademark rights. Our experience includes fast-casual chains, gourmet products, organic brands, and specialty food services across all market segments.",
      image: "https://images.pexels.com/photos/7627418/pexels-photo-7627418.jpeg"
    },
    {
      icon: <Palette className="w-12 h-12" />,
      title: "Creative & Media",
      description: "Specialized protection for creative professionals, production companies, publishing houses, media platforms, entertainment brands, photography studios, design agencies, and content creators. We protect brand names, show titles, character names, production company marks, and creative service brands. Our expertise includes digital media, streaming platforms, podcast networks, YouTube channels, and social media brands. We understand the creative industry's unique challenges and opportunities.",
      image: "https://images.unsplash.com/photo-1611241893603-3c359704e0ee"
    },
    {
      icon: <GraduationCap className="w-12 h-12" />,
      title: "Education & Training",
      description: "Comprehensive trademark protection for universities, colleges, K-12 schools, online learning platforms, training companies, certification programs, educational technology, tutoring services, and course creators. Protect institutional names, program titles, course brands, educational methodologies, training systems, and certification marks. We help traditional institutions and EdTech startups secure their educational brands and protect their intellectual property in an increasingly competitive market.",
      image: "https://images.pexels.com/photos/36159720/pexels-photo-36159720.jpeg"
    },
    {
      icon: <Dumbbell className="w-12 h-12" />,
      title: "Fitness & Sports",
      description: "Trademark services for gyms, fitness centers, personal trainers, sports organizations, athletic apparel brands, sports equipment manufacturers, fitness apps, and wellness programs. Protect gym names, workout programs, training methodologies, fitness brands, sports team names, athletic events, and sports equipment marks. We help franchises, independent studios, athletic brands, and sports organizations build protected brand portfolios that support expansion and licensing opportunities.",
      image: "https://images.pexels.com/photos/4716816/pexels-photo-4716816.jpeg"
    }
  ];

  return (
    <section id="industries" className="section-padding">
      <div className="container mx-auto px-4">
        <h2 className="section-title text-center">Industries We Serve</h2>
        <div className="w-24 h-1 bg-secondary mx-auto mb-4"></div>
        <p className="text-center text-gray-600 max-w-3xl mx-auto mb-16 text-lg">
          We provide specialized trademark registration services across diverse industries and business sectors. 
          Our attorneys understand industry-specific challenges and regulations, delivering tailored trademark 
          strategies that protect your unique brand identity. From startups to established enterprises, we've 
          helped thousands of businesses across every sector secure their trademark rights.
        </p>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {industries.map((industry, index) => (
            <Card key={index} className="card-hover overflow-hidden group">
              <div className="relative h-48 overflow-hidden">
                <img 
                  src={industry.image} 
                  alt={industry.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-primary/90 via-primary/50 to-transparent flex items-end p-6">
                  <div className="text-secondary">
                    {industry.icon}
                  </div>
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-primary mb-3">{industry.title}</h3>
                <p className="text-gray-700 leading-relaxed">{industry.description}</p>
              </div>
            </Card>
          ))}
        </div>

        <div className="mt-16 text-center">
          <p className="text-lg text-gray-700 mb-4">
            Don't see your industry listed? We work with businesses across all sectors.
          </p>
          <p className="text-secondary font-semibold">
            Contact us to discuss your specific trademark needs →
          </p>
        </div>
      </div>
    </section>
  );
};

export default IndustriesSection;
